import {
  FC,
  useState,
  useEffect,
  useRef,
  useCallback,
  useLayoutEffect,
} from 'react';
import Button from '@raydiant/raydial/components/Button';
import Popover from '@raydiant/raydial/components/Popover';
import Dialog from '@raydiant/raydial/components/Dialog';
import Text from '@raydiant/raydial/components/Text';
import { Ellipsis, Tag, Trash } from '@raydiant/raydial/components/Icons';
import ClockIcon from '../PlaylistItemV2/ClockIcon';
import ScheduleActiveIcon from '../PlaylistItemV2/ScheduleActiveIcon';
import ScaleBalancedIcon from '../PlaylistItemV2/ScaleBalancedIcon';
import CopyIcon from '../PlaylistItemV2/CopyIcon';
import SidebarMenuTooltip from '../../../../../components/Sidebar/SidebarMenuTooltip';

interface PlaylistNameInputProps {
  name?: string;
  onNameChange?: (name: string) => void;
  onNameBlur?: () => void;
  onScheduleClick?: () => void;
  onRulesClick?: () => void;
  onTagsClick?: () => void;
  onDuplicateClick?: () => void;
  onDeleteClick?: () => void;
  hasSchedule?: boolean;
  hasError?: boolean;
  isDisabled?: boolean;
  shouldAutoSelectName?: boolean;
}

const actionBtnClass =
  'w-12 h-12 flex items-center justify-center rounded-lg border-2 border-[#EAEAEA] text-[#343437] hover:bg-[#EAEAEA] transition-colors';

const PlaylistNameInput: FC<PlaylistNameInputProps> = ({
  name: controlledName,
  onNameChange,
  onNameBlur,
  onScheduleClick,
  onRulesClick,
  onTagsClick,
  onDuplicateClick,
  onDeleteClick,
  hasSchedule = false,
  hasError = false,
  isDisabled,
  shouldAutoSelectName = false,
}) => {
  const [localName, setLocalName] = useState(controlledName ?? '');
  const [isMoreOpen, setIsMoreOpen] = useState(false);
  const [moreTooltipOpen, setMoreTooltipOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const didAutoFocusRef = useRef(false);
  const moreTriggerRef = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    if (isMoreOpen) setMoreTooltipOpen(false);
  }, [isMoreOpen]);

  useEffect(() => {
    if (controlledName !== undefined) {
      setLocalName(controlledName);
    }
  }, [controlledName]);

  useLayoutEffect(() => {
    if (!shouldAutoSelectName) return;
    if (didAutoFocusRef.current || isDisabled) return;
    if (!inputRef.current) return;

    // Wait until controlled name has been hydrated, then select whole text.
    requestAnimationFrame(() => {
      if (!inputRef.current) return;
      inputRef.current.focus();
      inputRef.current.setSelectionRange(0, inputRef.current.value.length);
      didAutoFocusRef.current = true;
    });
  }, [isDisabled, localName, shouldAutoSelectName]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setLocalName(value);
    onNameChange?.(value);
  };

  const handleActionClick = useCallback((action?: () => void) => {
    if (!action) return;
    setIsMoreOpen(false);
    action();
  }, []);

  return (
    <div className="bg-white rounded-2xl p-2.5 mb-6 shrink-0">
      <div className="flex items-center gap-2">
        <input
          ref={inputRef}
          type="text"
          value={localName}
          onChange={handleChange}
          onBlur={onNameBlur}
          placeholder="Playlist name"
          disabled={isDisabled}
          className={`flex-1 h-12 px-4 bg-gray-100 rounded-2xl text-black text-base outline-none max-w-[440px] disabled:opacity-50 ${
            hasError
              ? 'ring-2 ring-red-500'
              : 'focus:ring-2 focus:ring-blue-500'
          }`}
        />

        <SidebarMenuTooltip title="Schedule" placement="bottom" arrow>
          <span>
            <Button
              variant="unstyled"
              className={actionBtnClass}
              onPress={onScheduleClick}
              isDisabled={isDisabled}
            >
              {hasSchedule ? (
                <ScheduleActiveIcon size={28} />
              ) : (
                <ClockIcon size={16} />
              )}
            </Button>
          </span>
        </SidebarMenuTooltip>
        <div className="flex-1" />
        <SidebarMenuTooltip
          title="More actions"
          placement="bottom"
          arrow
          open={moreTooltipOpen && !isMoreOpen}
          disableHoverListener
          disableFocusListener
          disableTouchListener
        >
          <span
            onMouseEnter={() => {
              if (!isMoreOpen) setMoreTooltipOpen(true);
            }}
            onMouseLeave={() => setMoreTooltipOpen(false)}
          >
            <Button
              variant="unstyled"
              className={actionBtnClass}
              onPress={() => setIsMoreOpen(true)}
              isDisabled={isDisabled}
            >
              <span ref={moreTriggerRef}>
                <Ellipsis size={16} />
              </span>
            </Button>
          </span>
        </SidebarMenuTooltip>
      </div>
      {hasError && (
        <Text variant="caption" className="text-red-600 mt-1 ml-2">
          Playlist name is required
        </Text>
      )}

      <Popover
        triggerRef={moreTriggerRef}
        isOpen={isMoreOpen}
        onOpenChange={setIsMoreOpen}
        className="w-[194px]"
        placement="bottom end"
      >
        <Dialog className="px-0 py-2" aria-label="playlist actions">
          <div className="overflow-hidden">
            {onRulesClick && (
              <Button
                className="w-full flex items-center gap-2 py-2.5 px-4 cursor-pointer hover:bg-gray-200 min-h-[40px]"
                variant="unstyled"
                onPress={() => handleActionClick(onRulesClick)}
              >
                <div className="flex shrink-0 items-center justify-center w-[17px] h-[17px]">
                  <ScaleBalancedIcon size={17} />
                </div>
                <span className="flex items-center pt-0.5 text-base leading-tight">
                  Rules
                </span>
              </Button>
            )}

            {onTagsClick && (
              <Button
                className="w-full flex items-center gap-2 py-2.5 px-4 cursor-pointer hover:bg-gray-200 min-h-[40px]"
                variant="unstyled"
                onPress={() => handleActionClick(onTagsClick)}
              >
                <div className="flex shrink-0 items-center justify-center w-[17px] h-[17px]">
                  <Tag size={17} />
                </div>
                <span className="flex items-center pt-0.5 text-base leading-tight">
                  Tags
                </span>
              </Button>
            )}

            {onDuplicateClick && (
              <Button
                className="w-full flex items-center gap-2 py-2.5 px-4 cursor-pointer hover:bg-gray-200 min-h-[40px]"
                variant="unstyled"
                onPress={() => handleActionClick(onDuplicateClick)}
              >
                <div className="flex shrink-0 items-center justify-center w-[17px] h-[17px]">
                  <CopyIcon size={17} />
                </div>
                <span className="flex items-center pt-0.5 text-base leading-tight">
                  Duplicate
                </span>
              </Button>
            )}

            {onDeleteClick && (
              <Button
                className="w-full flex items-center gap-2 py-2.5 px-4 cursor-pointer hover:bg-gray-200 min-h-[40px] text-red-700"
                variant="unstyled"
                onPress={() => handleActionClick(onDeleteClick)}
              >
                <div className="flex shrink-0 items-center justify-center w-[17px] h-[17px]">
                  <Trash size={17} />
                </div>
                <span className="flex items-center pt-0.5 text-base leading-tight">
                  Delete
                </span>
              </Button>
            )}
          </div>
        </Dialog>
      </Popover>
    </div>
  );
};

export default PlaylistNameInput;
